import axios from 'axios';
import pick from 'lodash/pick';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  createStripeAccount,
  fetchStripeAccount,
  updateStripeAccount,
} from '../../ducks/stripeConnectAccount.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getAllTransitionsForEveryProcess } from '../../transactions/transaction';
// TAP PAYMENT - COMMENTED OUT (Replaced with STC Bank)
// import { onCreateBusiness } from '../../util/api'; // Tap-specific business creation
import { apiBaseUrl, onFetchMerchantPayoutApplication, onCreateMerchantPayoutApplication, onUpdateMerchantPayoutApplication, onGetRequestedPayouts, onGetTotalBalance, onRequestPayout, sendMailToUser } from '../../util/api';
import { storableError } from '../../util/errors';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

const INITIAL_PAGE_SIZE = 20;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/StripePayoutPage/SET_INITIAL_VALUES';
export const SAVE_PAYOUT_DETAILS_REQUEST = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_REQUEST';
export const SAVE_PAYOUT_DETAILS_SUCCESS = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_SUCCESS';
export const SAVE_PAYOUT_DETAILS_ERROR = 'app/StripePayoutPage/SAVE_PAYOUT_DETAILS_ERROR';

export const FETCH_BALANCE_REQUEST = 'app/StripePayoutPage/FETCH_BALANCE_REQUEST';
export const FETCH_BALANCE_SUCCESS = 'app/StripePayoutPage/FETCH_BALANCE_SUCCESS';
export const FETCH_BALANCE_ERROR = 'app/StripePayoutPage/FETCH_BALANCE_ERROR';

export const FETCH_PAYOUTS_REQUEST = 'app/StripePayoutPage/FETCH_PAYOUTS_REQUEST';
export const FETCH_PAYOUTS_SUCCESS = 'app/StripePayoutPage/FETCH_PAYOUTS_SUCCESS';
export const FETCH_PAYOUTS_ERROR = 'app/StripePayoutPage/FETCH_PAYOUTS_ERROR';

export const CREATE_PAYOUT_REQUEST = 'app/StripePayoutPage/CREATE_PAYOUT_REQUEST';
export const CREATE_PAYOUT_SUCCESS = 'app/StripePayoutPage/CREATE_PAYOUT_SUCCESS';
export const CREATE_PAYOUT_ERROR = 'app/StripePayoutPage/CREATE_PAYOUT_ERROR';

export const FETCH_SALES_REQUEST = 'app/InboxPage/FETCH_SALES_REQUEST';
export const FETCH_SALES_SUCCESS = 'app/InboxPage/FETCH_SALES_SUCCESS';
export const FETCH_SALES_ERROR = 'app/InboxPage/FETCH_SALES_ERROR';

export const FETCH_PAYOUT_APPLICATION_REQUEST = 'app/StripePayoutPage/FETCH_PAYOUT_APPLICATION_REQUEST';
export const FETCH_PAYOUT_APPLICATION_SUCCESS = 'app/StripePayoutPage/FETCH_PAYOUT_APPLICATION_SUCCESS';
export const FETCH_PAYOUT_APPLICATION_ERROR = 'app/StripePayoutPage/FETCH_PAYOUT_APPLICATION_ERROR';

export const UPDATE_PAYOUT_APPLICATION_REQUEST = 'app/StripePayoutPage/UPDATE_PAYOUT_APPLICATION_REQUEST';
export const UPDATE_PAYOUT_APPLICATION_SUCCESS = 'app/StripePayoutPage/UPDATE_PAYOUT_APPLICATION_SUCCESS';
export const UPDATE_PAYOUT_APPLICATION_ERROR = 'app/StripePayoutPage/UPDATE_PAYOUT_APPLICATION_ERROR';

export const CREATE_PAYOUT_APPLICATION_REQUEST = 'app/StripePayoutPage/CREATE_PAYOUT_APPLICATION_REQUEST';
export const CREATE_PAYOUT_APPLICATION_SUCCESS = 'app/StripePayoutPage/CREATE_PAYOUT_APPLICATION_SUCCESS';
export const CREATE_PAYOUT_APPLICATION_ERROR = 'app/StripePayoutPage/CREATE_PAYOUT_APPLICATION_ERROR';

// ================ Reducer ================ //

const initialState = {
  payoutDetailsSaveInProgress: false,
  payoutDetailsSaved: false,
  fromReturnURL: false,

  fetchBalanceRequest: false,
  fetchBalanceSuccess: null,
  fetchBalanceError: null,

  fetchPayoutsRequest: false,
  fetchPayoutsSuccess: null,
  fetchPayoutsError: null,

  createPayoutRequest: false,
  createPayoutSuccess: null,
  createPayoutError: null,

  fetchSalesInProgress: false,
  fetchSalesError: null,
  pagination: null,
  transactionRefs: [],

  fetchPayoutApplicationInProgress: false,
  payoutApplication: null,
  fetchPayoutApplicationError: null,
  payoutApplicationFetched: false, // Track if we've attempted to fetch

  updatePayoutApplicationInProgress: false,
  updatePayoutApplicationError: null,

  createPayoutApplicationInProgress: false,
  createPayoutApplicationError: null,
};


const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx.attributes ? tx.attributes.lastTransitionedAt : null;
    })
  );

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));


export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SAVE_PAYOUT_DETAILS_REQUEST:
      return { ...state, payoutDetailsSaveInProgress: true };
    case SAVE_PAYOUT_DETAILS_ERROR:
      return { ...state, payoutDetailsSaveInProgress: false };
    case SAVE_PAYOUT_DETAILS_SUCCESS:
      return { ...state, payoutDetailsSaveInProgress: false, payoutDetailsSaved: true };

    case FETCH_BALANCE_REQUEST:
      return { ...state, fetchBalanceRequest: true };
    case FETCH_BALANCE_SUCCESS:
      return { ...state, fetchBalanceSuccess: payload, fetchBalanceRequest: false };
    case FETCH_BALANCE_ERROR:
      return { ...state, fetchBalanceError: false };

    case FETCH_PAYOUTS_REQUEST:
      return { ...state, fetchPayoutsRequest: true };
    case FETCH_PAYOUTS_SUCCESS:
      return { ...state, fetchPayoutsSuccess: payload, fetchPayoutsRequest: false };
    case FETCH_PAYOUTS_ERROR:
      return { ...state, fetchPayoutsError: false };

    case CREATE_PAYOUT_REQUEST:
      return { ...state, createPayoutRequest: true };
    case CREATE_PAYOUT_SUCCESS:
      return { ...state, createPayoutSuccess: payload, createPayoutRequest: false };
    case CREATE_PAYOUT_ERROR:
      return { ...state, createPayoutError: false };

    case FETCH_SALES_REQUEST:
      return { ...state, fetchSalesInProgress: true, fetchSalesError: null };
    case FETCH_SALES_SUCCESS: {
      const transactions = sortedTransactions(payload.data.data);
      return {
        ...state,
        fetchSalesInProgress: false,
        transactionRefs: entityRefs(transactions),
        pagination: payload.data.meta,
      };
    }
    case FETCH_SALES_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchSalesInProgress: false, fetchSalesError: payload };

    case FETCH_PAYOUT_APPLICATION_REQUEST:
      return { ...state, fetchPayoutApplicationInProgress: true, fetchPayoutApplicationError: null };
    case FETCH_PAYOUT_APPLICATION_SUCCESS:
      return { ...state, fetchPayoutApplicationInProgress: false, payoutApplication: payload, payoutApplicationFetched: true };
    case FETCH_PAYOUT_APPLICATION_ERROR:
      return { ...state, fetchPayoutApplicationInProgress: false, fetchPayoutApplicationError: payload, payoutApplicationFetched: true };

    case UPDATE_PAYOUT_APPLICATION_REQUEST:
      return { ...state, updatePayoutApplicationInProgress: true, updatePayoutApplicationError: null };
    case UPDATE_PAYOUT_APPLICATION_SUCCESS:
      return { ...state, updatePayoutApplicationInProgress: false, payoutApplication: payload };
    case UPDATE_PAYOUT_APPLICATION_ERROR:
      return { ...state, updatePayoutApplicationInProgress: false, updatePayoutApplicationError: payload };

    case CREATE_PAYOUT_APPLICATION_REQUEST:
      return { ...state, createPayoutApplicationInProgress: true, createPayoutApplicationError: null };
    case CREATE_PAYOUT_APPLICATION_SUCCESS:
      return { ...state, createPayoutApplicationInProgress: false, payoutApplication: payload, payoutApplicationFetched: true };
    case CREATE_PAYOUT_APPLICATION_ERROR:
      return { ...state, createPayoutApplicationInProgress: false, createPayoutApplicationError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const savePayoutDetailsRequest = () => ({
  type: SAVE_PAYOUT_DETAILS_REQUEST,
});
export const savePayoutDetailsError = () => ({
  type: SAVE_PAYOUT_DETAILS_ERROR,
});
export const savePayoutDetailsSuccess = () => ({
  type: SAVE_PAYOUT_DETAILS_SUCCESS,
});

export const fetchBalanceRequest = () => ({
  type: FETCH_BALANCE_REQUEST,
});
export const fetchBalanceSuccess = (data) => ({
  type: FETCH_BALANCE_SUCCESS,
  payload: data
});
export const fetchBalanceError = (err) => ({
  type: FETCH_BALANCE_ERROR,
  payload: err
});

// FETCH REQUESTED PAYOUTS
export const fetchPayoutsRequest = () => ({
  type: FETCH_PAYOUTS_REQUEST,
});
export const fetchPayoutsSuccess = (data) => ({
  type: FETCH_PAYOUTS_SUCCESS,
  payload: data
});
export const fetchPayoutsError = (err) => ({
  type: FETCH_PAYOUTS_ERROR,
  payload: err
});

// CREATE PAYOUT
export const createPayoutRequest = () => ({
  type: CREATE_PAYOUT_REQUEST,
});
export const createPayoutSuccess = (data) => ({
  type: CREATE_PAYOUT_SUCCESS,
  payload: data
});
export const createPayoutError = (err) => ({
  type: CREATE_PAYOUT_ERROR,
  payload: err
});

// Transactions
const fetchSalesRequest = () => ({ type: FETCH_SALES_REQUEST });
const fetchSalesSuccess = response => ({
  type: FETCH_SALES_SUCCESS,
  payload: response,
});

const fetchSalesError = e => ({
  type: FETCH_SALES_ERROR,
  error: true,
  payload: e,
});

// Upload file to tap
export const uploadFileToTap = async (data) => {
  const { purpose, title, expires_at, file_link_create, file } = data;

  try {
    const formData = new FormData();
    formData.append('purpose', purpose);
    formData.append('file', file);
    formData.append('title', title);
    formData.append('expires_at', expires_at);
    formData.append('file_link_create', file_link_create);

    const url = apiBaseUrl() + `/api/upload-file-to-tap`;

    const response = await axios.post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    // Check if response contains an error field
    if (response.data && response.data.error) {
      throw new Error(`${response.data.error}`);
    }
    return response.data;
  } catch (error) {
    // Handle axios or server errors
    if (error.response) {
      // Server error (e.g., 4xx/5xx status codes)
      throw new Error(`${error.response.data.message || 'Unknown error'}`);
    } else if (error.request) {
      // No response received (e.g., network issue)
      throw new Error('No response received from the server');
    } else {
      // Other errors (e.g., axios config issues)
      throw new Error(`Error during file upload: ${error.message}`);
    }
  }
};

// TAP PAYMENT - COMMENTED OUT (Replaced with STC Bank)
// create business in Tap
// eslint-disable-next-line no-unused-vars
export const createTapBusiness = (data) => async (dispatch, getState, sdk) => {
  try {
    // TAP PAYMENT - COMMENTED OUT (Replaced with STC Bank)
    // const onCreateBusiness = require('../../util/api').onCreateBusiness; // TAP - commented out
    // const response = await onCreateBusiness(data);
    // const businessData = response?.data || response;
    //
    // // Check for business data and essential properties
    // if (businessData && businessData.id && businessData.destination_id) {
    //   // Dispatch the updateProfile action
    //   await dispatch(
    //     updateProfile({
    //       publicData: {
    //         destinationId: businessData.destination_id,
    //         businessId: businessData.id,
    //         isPayoutCreated: true,
    //       },
    //     })
    //   );
    //
    //   // Return the business data
    //   return businessData;
    // } else {
    //   // If the expected data is not available, throw an error
    //   throw new Error('Missing destination_id or id in response');
    // }
    
    // Tap business creation is disabled - return error
    throw new Error('Tap business creation is disabled. Please use STC Bank for merchant onboarding.');
  } catch (error) {
    // Detailed error handling
    if (error.response) {
      // Handle server-side errors (e.g., 4xx/5xx status codes)
      throw new Error(`${error.response.data.message || 'Unknown error'}`);
    } else if (error.request) {
      // Handle cases where no response is received (e.g., network issues)
      throw new Error('No response received from the server');
    } else {
      // Handle any other errors (e.g., Axios configuration issues)
      throw new Error(`${error.message || 'Unknown error'}`);
    }
  }
};


// ================ Thunks ================ //

export const savePayoutDetails = (values, isUpdateCall) => (dispatch, getState, sdk) => {
  const upsertThunk = isUpdateCall ? updateStripeAccount : createStripeAccount;
  dispatch(savePayoutDetailsRequest());

  return dispatch(upsertThunk(values, { expand: true }))
    .then(response => {
      dispatch(savePayoutDetailsSuccess());
      return response;
    })
    .catch(() => dispatch(savePayoutDetailsError()));
};

export const loadData = (params, search) => async (dispatch, getState, sdk) => {
  dispatch(setInitialValues());

  try {
    const currentUserResponse = await dispatch(fetchCurrentUser());
    const currentUser = getState().user.currentUser;
    const currentUserId = currentUser?.id?.uuid;

    if (!currentUserId) {
      throw new Error('Current user not found');
    }

    await Promise.all([
      dispatch(fetchTotalBalance(currentUserId)),
      // dispatch(fetchPayouts(currentUserId)) // Uncomment if needed
    ]);

    if (currentUser?.stripeAccount) {
      dispatch(fetchStripeAccount());
    }

    dispatch(fetchSalesRequest());

    const { page = 1 } = parse(search);

    const apiQueryParams = {
      only: "sale",
      lastTransitions: getAllTransitionsForEveryProcess(),
      processNames: ['default-booking', 'default-deal-booking', 'default-purchase', 'default-deal-purchase'],
      include: [
        'listing',
        'provider',
        'provider.profileImage',
        'customer',
        'customer.profileImage',
        'booking',
      ],
      'fields.transaction': [
        'createdAt',
        'processName',
        'lastTransition',
        'lastTransitionedAt',
        'transitions',
        'payinTotal',
        'payoutTotal',
        'lineItems',
        'protectedData',
      ],
      'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      page,
      perPage: INITIAL_PAGE_SIZE,
    };

    const response = await sdk.transactions.query(apiQueryParams);
    dispatch(addMarketplaceEntities(response));
    dispatch(fetchSalesSuccess(response));
    return response;
  } catch (e) {
    dispatch(fetchSalesError(storableError(e)));
    throw e;
  }
};


export const createPayout = (currentUserId) => async (dispatch, getState, sdk) => {
  try {
    dispatch(createPayoutRequest());
    const response = await onRequestPayout({ provider_id: currentUserId });
    dispatch(createPayoutSuccess(response?.data));


    await Promise.all([
      dispatch(fetchTotalBalance(currentUserId)),
      dispatch(fetchPayouts(currentUserId))
    ]);
    return response;
  }
  catch (err) {
    dispatch(fetchPayoutsError(err));
  }
}

const fetchPayouts = (currentUserId) => async (dispatch, getState, sdk) => {
  try {
    dispatch(fetchPayoutsRequest());
    const response = await onGetRequestedPayouts({ provider_id: currentUserId });
    dispatch(fetchPayoutsSuccess(response?.data || []));
  }
  catch (err) {
    dispatch(fetchPayoutsError(err));
  }
}

const fetchTotalBalance = (currentUserId) => async (dispatch, getState, sdk) => {
  try {
    dispatch(fetchBalanceRequest());
    const response = await onGetTotalBalance({ provider_id: currentUserId });
    dispatch(fetchBalanceSuccess(response));
  }
  catch (err) {
    dispatch(fetchBalanceError(err));
  }
}

// Fetch Payout Application Action Creators
const fetchPayoutApplicationRequest = () => ({
  type: FETCH_PAYOUT_APPLICATION_REQUEST,
});

const fetchPayoutApplicationSuccess = (data) => ({
  type: FETCH_PAYOUT_APPLICATION_SUCCESS,
  payload: data,
});

const fetchPayoutApplicationError = (err) => ({
  type: FETCH_PAYOUT_APPLICATION_ERROR,
  payload: err,
});

// Update Payout Application Action Creators
const updatePayoutApplicationRequest = () => ({
  type: UPDATE_PAYOUT_APPLICATION_REQUEST,
});

const updatePayoutApplicationSuccess = (data) => ({
  type: UPDATE_PAYOUT_APPLICATION_SUCCESS,
  payload: data,
});

const updatePayoutApplicationError = (err) => ({
  type: UPDATE_PAYOUT_APPLICATION_ERROR,
  payload: err,
});

// Create Payout Application Action Creators
const createPayoutApplicationRequest = () => ({
  type: CREATE_PAYOUT_APPLICATION_REQUEST,
});

const createPayoutApplicationSuccess = (data) => ({
  type: CREATE_PAYOUT_APPLICATION_SUCCESS,
  payload: data,
});

const createPayoutApplicationError = (err) => ({
  type: CREATE_PAYOUT_APPLICATION_ERROR,
  payload: err,
});

// Fetch Payout Application Thunk
export const fetchPayoutApplication = (currentUserId) => async (dispatch, getState, sdk) => {
  if (!currentUserId) {
    return;
  }

  dispatch(fetchPayoutApplicationRequest());
  
  try {
    const response = await onFetchMerchantPayoutApplication({ userId: currentUserId });
    
    if (response?.success && response?.application) {
      dispatch(fetchPayoutApplicationSuccess(response.application));
      return response.application;
    } else {
      dispatch(fetchPayoutApplicationSuccess(null));
      return null;
    }
  } catch (err) {
    console.error('Error fetching payout application:', err);
    dispatch(fetchPayoutApplicationError(err));
    return null;
  }
};

// Update Payout Application Thunk
export const updatePayoutApplication = (applicationData) => async (dispatch, getState, sdk) => {
  if (!applicationData || !applicationData.userId) {
    throw new Error('Application data and userId are required');
  }

  dispatch(updatePayoutApplicationRequest());
  
  try {
    const response = await onUpdateMerchantPayoutApplication(applicationData);
    
    if (response?.success && response?.application) {
      dispatch(updatePayoutApplicationSuccess(response.application));
      return response.application;
    } else {
      throw new Error(response?.message || 'Failed to update application');
    }
  } catch (err) {
    console.error('Error updating payout application:', err);
    dispatch(updatePayoutApplicationError(err));
    throw err;
  }
};

// Create Payout Application Thunk
export const createPayoutApplication = (applicationData) => async (dispatch, getState, sdk) => {
  if (!applicationData) {
    throw new Error('Application data is required');
  }

  dispatch(createPayoutApplicationRequest());
  
  try {
    const response = await onCreateMerchantPayoutApplication(applicationData);
    
    if (response?.success && response?.application) {
      dispatch(createPayoutApplicationSuccess(response.application));
      return response.application;
    } else {
      throw new Error(response?.message || 'Failed to create application');
    }
  } catch (err) {
    console.error('Error creating payout application:', err);
    dispatch(createPayoutApplicationError(err));
    throw err;
  }
};
